import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import type { Options } from '@contentful/rich-text-react-renderer';
import { BLOCKS } from '@contentful/rich-text-types';
import { Box, Col } from '@opendoor/atoms/box';
import { Image } from '@opendoor/atoms/image';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import type { EntryComponent } from 'cms/entries/entries';
import type { ILpComponentComparisonTable } from 'declarations/contentful';
import { optimizeContentfulImageUrl } from 'helpers/imageUrl';

import Container from './shared/Container';
import { TitleWithAccent } from './shared/Typography';

import styles from './ComparisonTable.module.css';

const Yes = () => (
  <Text as="p" className="flex items-center justify-center text-body-lg text-inherit">
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M2 8.03846L6 11.5L14 4" stroke="currentColor" strokeWidth="2" />
    </svg>
  </Text>
);

const No = () => (
  <Text as="p" className="flex items-center justify-center text-body-lg text-inherit">
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M3 13.0007L13 3" stroke="currentColor" strokeWidth="2" />
      <path d="M13 13L8.00005 7.99984L3 3" stroke="currentColor" strokeWidth="2" />
    </svg>
  </Text>
);

const Question = () => (
  <Text as="p" className="flex items-center justify-center text-subheader-md text-inherit">
    ?
  </Text>
);

const Dash = () => (
  <Text as="p" className="flex items-center justify-center text-subheader-md text-inherit">
    -
  </Text>
);

const options: Options = {
  renderNode: {
    [BLOCKS.TABLE]: (_, children) => children,
    [BLOCKS.TABLE_ROW]: (_, children) => <tr>{children}</tr>,
    [BLOCKS.TABLE_HEADER_CELL]: (_, children) => <th>{children}</th>,
    [BLOCKS.TABLE_CELL]: (_: any, children: any) => {
      if (children[0].type == 'u') {
        return <th scope="row">{children[0].props.children}</th>;
      }
      return <td>{children}</td>;
    },
    [BLOCKS.PARAGRAPH]: (_: any, children: any) => {
      if (children[0] == '✓') return <Yes />;
      if (children[0] == 'X') return <No />;
      if (children[0] == '?') return <Question />;
      if (children[0] == '-') return <Dash />;
      return children[0];
    },
  },
};

const RenderComparisonTable = (entry: ILpComponentComparisonTable) => {
  const { fields } = entry;
  const tableId = `comparison-table-${entry.sys.id}`;
  // Contentful field is typed as 'Brand Blue 600' | 'Warm Grey 950' — map to semantic tokens
  const highlightColor =
    fields.highlightColor === 'Warm Grey 950'
      ? 'var(--color-od-bg-inverse-primary)'
      : 'var(--color-od-content-accent-brand)';
  const hasBorder =
    fields.layout === 'Border' || fields.layout === 'Earth 200' || fields.layout === 'Grey';
  const isNoBorder = fields.layout === 'No Border';
  const hasBGAndBorder =
    fields.image && (fields.layout === 'Earth 200' || fields.layout === 'Grey');
  const alignClass = fields.tableTextAlign === 'Center' ? styles.centerAlign : styles.leftAlign;
  const imageUrl = fields.image?.fields?.file?.url;
  const optimizedImageUrl = imageUrl
    ? optimizeContentfulImageUrl(imageUrl, { width: 1200, quality: 80 })
    : undefined;

  const tableClasses = [
    styles.table,
    alignClass,
    hasBorder && styles.borderLayout,
    isNoBorder && styles.noBorderLayout,
  ]
    .filter(Boolean)
    .join(' ');

  // CSS variables consumed by ComparisonTable.module.css for the layout variants.
  const cssVars = {
    '--border-primary': 'var(--color-od-border-primary)',
    '--accent-bg': 'var(--color-od-bg-accent-brand)',
  } as React.CSSProperties;

  // Dynamic nth-child selectors for highlight/opendoor columns (can't use CSS variables in nth-child)
  let columnCss = '';
  if (fields.highlightColumn) {
    const col = fields.highlightColumn;
    columnCss += `
      #${tableId} th:not([scope=row]):nth-child(${col}) { background-color: ${highlightColor}; color: var(--color-od-content-inverse-primary); }
      #${tableId} td:nth-child(${col}) { border-left: 1px solid ${highlightColor}; border-right: 1px solid ${highlightColor}; }
      #${tableId} tbody tr:last-child td:nth-child(${col}) { border-bottom: 1px solid ${highlightColor}; }
    `;
  }
  if (fields.opendoorColumn) {
    columnCss += `
      #${tableId} tbody td:nth-child(${fields.opendoorColumn}) { color: var(--color-od-content-accent-brand); }
    `;
  }

  return (
    <Container className="py-6">
      {fields.title && (
        <TitleWithAccent
          title={fields.title}
          titleAccent={fields.titleAccent}
          titleSize={fields.titleSize}
          mb="$16"
          mx={fields.titleAlign === 'Center' ? 'auto' : 0}
          textAlign={fields.titleAlign === 'Center' ? 'center' : 'left'}
        />
      )}
      <Box
        className={cn(
          'block gap-5',
          hasBGAndBorder ? 'rounded-2xl bg-od-bg-tertiary p-5' : 'p-0',
          fields.image ? 'lg:flex lg:flex-row' : 'lg:block',
        )}
      >
        <Col className={cn(fields.image && 'grow-6 basis-0')}>
          <Col
            className={cn(
              'overflow-auto rounded-2xl',
              fields.layout === 'Border' && 'border border-od-border-primary',
            )}
          >
            {columnCss && <style dangerouslySetInnerHTML={{ __html: columnCss }} />}
            <table id={tableId} className={tableClasses} style={cssVars}>
              {fields.tableHeader && (
                <thead>{documentToReactComponents(fields.tableHeader, options)}</thead>
              )}
              {fields.tableBody && (
                <tbody>{documentToReactComponents(fields.tableBody, options)}</tbody>
              )}
            </table>
          </Col>
        </Col>
        {optimizedImageUrl && fields.image && (
          <Col className="grow-4 basis-0">
            <Image
              className={styles.tableImage}
              src={optimizedImageUrl}
              alt={fields.image.fields?.description || fields.image.fields?.title || ''}
              loading="lazy"
              decoding="async"
            />
          </Col>
        )}
      </Box>
    </Container>
  );
};

const ComparisonTable: EntryComponent<ILpComponentComparisonTable> = {
  render: RenderComparisonTable,
};

export default ComparisonTable;
